import { ReactElement, useState } from 'react'
import styled from 'styled-components'

import Dropdown from 'components/v2/Dropdown'
import { Input } from 'components/Input'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { CircleCloseIcon } from '@10xLabs/react-web/components/Icons/CircleClose'
import { DayPicker } from 'components/v2/DayPicker'
import { TimePicker } from 'components/TimePicker'
import { device } from 'utils/style'
import { dateToString } from 'utils/date'
import { IntlShape } from 'react-intl'
import { IInputDateTimeProps } from './types'

const StyledDatePicker = styled.div<TSFixMe>`
  border-top: 1px solid #f2f2f2;
  padding: 20px 0 0;
  display: flex;
  justify-content: center;

  > div {
    font-size: 1.1rem;
  }

  @media ${device.tablet} {
    display: block;
    padding: 10px;
  }
`

const TogglePicker = styled.div<TSFixMe>`
  padding: 10px;
  cursor: pointer;
`

const Split = styled.div<TSFixMe>`
  width: 100%;
  height: 1px;
  opacity: 0.3;
  background-color: #9999bd;
`

const CloseIconWrapper = styled.div<TSFixMe>`
  display: flex;

  svg {
    fill: #9999bd;
    width: 18px;
    height: 18px;
  }
`

const dateTimeToString = (intl: IntlShape, date: Date): string => {
  if (!date) return ''

  if (!isFinite(date.getTime())) {
    return 'Invalid Date'
  }

  const dateString = dateToString(date)
  const timeString = intl
    .formatTime(date, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
    .replace(/\.\s?/g, '')
    .toUpperCase()
    .replace('00:', '12:')

  return `${dateString} ${timeString}`
}

export const InputDateTime = (props: IInputDateTimeProps): ReactElement => {
  const {
    label,
    onChange,
    value,
    placeholder,
    touched,
    errors,
    id,
    intl,
    disabledDays,
    month,
    optional,
    intervalMinutes = 1,
    readOnly,
    disabled
  } = props

  const [isOpen, setOpen] = useState(false)
  const [isShowingTime, showTime] = useState(false)

  const handleDateChange = (newDate?: Date): void => {
    if (newDate) {
      if (value) {
        newDate.setHours(value.getHours())
        newDate.setMinutes(value.getMinutes())
      }

      onChange(newDate)
    }
  }

  const handleReset = (event: React.SyntheticEvent): void => {
    event.stopPropagation()
    event.preventDefault()

    onChange()
  }

  const dateTimeStr = dateTimeToString(intl, value)
  const showReset = dateTimeStr && optional

  return (
    <>
      <Dropdown.Wrapper onClickAway={(): void => setOpen(false)} form>
        <Input
          id={id}
          value={dateTimeStr}
          onChange={(): void => undefined}
          onClick={(): void => setOpen(!isOpen)}
          placeholder={placeholder}
          errors={errors}
          touched={touched}
          label={label}
          iconRight={
            showReset && (
              <CloseIconWrapper onClick={handleReset}>
                <CircleCloseIcon />
              </CloseIconWrapper>
            )
          }
          disabled={disabled}
        />

        {!readOnly && !disabled && (
          <Dropdown.Content open={isOpen} noPadding right fitContent form center noOverflow>
            {isShowingTime ? (
              <TogglePicker onClick={(): void => showTime(false)}>
                <FormattedDate intl={intl} value={value} month="2-digit" day="2-digit" year="numeric" />
              </TogglePicker>
            ) : (
              <StyledDatePicker>
                <DayPicker value={value} onChange={handleDateChange} disabledDays={disabledDays} month={month} />
              </StyledDatePicker>
            )}

            <Split />

            {isShowingTime ? (
              <TimePicker value={value} onChange={onChange} intervalMinutes={intervalMinutes} />
            ) : (
              <TogglePicker onClick={(): void => showTime(true)}>
                {value ? <FormattedTimeByDate date={value} intl={intl} /> : '--:-- --'}
              </TogglePicker>
            )}
          </Dropdown.Content>
        )}
      </Dropdown.Wrapper>
    </>
  )
}
